import { useRef } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Camera, Mic } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { useUploadDocument } from '../api/uploaded-documents-api';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../constants';

/**
 * Personal-edition Home hero: two mobile-friendly action tiles above the
 * household member list.
 *
 *  - Quick Start → /quick-start (record a visit).
 *  - Scan       → opens the rear camera (mobile) or a file picker (desktop),
 *                 uploads the captured image to the Inbox, and routes the
 *                 user there to adjudicate it onto a household member.
 */
export function PersonalHomeTiles() {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadDocument();

  const handleCameraFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input value early so picking the same file again still fires
    // change events, no matter which exit branch we take below.
    e.target.value = '';
    if (!file) return;

    if (!isLikelyImage(file)) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Image too large. Max ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'Scan';

    try {
      await upload.mutateAsync({
        file,
        name: baseName,
        document_type: 'other',
        document_date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast.success('Photo uploaded — it will appear in your Inbox.');
      navigate({ to: '/documents' });
    } catch {
      toast.error('Upload failed. Please try again.');
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => navigate({ to: '/quick-start' })}
        className="group flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card p-6 text-center transition hover:bg-accent active:scale-[0.98]">
        <Mic className="h-10 w-10 text-primary" />
        <div>
          <p className="font-semibold">Quick Start</p>
          <p className="text-xs text-muted-foreground">Record a visit</p>
        </div>
      </button>

      <button
        type="button"
        disabled={upload.isPending}
        onClick={() => cameraInputRef.current?.click()}
        className="group flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card p-6 text-center transition hover:bg-accent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60">
        <Camera className="h-10 w-10 text-primary" />
        <div>
          <p className="font-semibold">{upload.isPending ? 'Uploading…' : 'Scan'}</p>
          <p className="text-xs text-muted-foreground">Upload from camera</p>
        </div>
      </button>

      <input
        ref={cameraInputRef}
        type="file"
        // `image/*` covers most types, but iOS HEIC/HEIF often reports an
        // empty MIME on the File object — accept the explicit subtypes too
        // so the picker doesn't grey them out.
        accept="image/*,image/heic,image/heif"
        capture="environment"
        className="hidden"
        onChange={handleCameraFile}
      />
    </div>
  );
}

const _IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];

/**
 * Loose image check. `file.type` is the source of truth when present, but
 * iOS Safari reports an empty string for HEIC files captured from the
 * camera, so we fall back to the file extension.
 */
function isLikelyImage(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  if (file.type) return false;
  const lower = file.name.toLowerCase();
  return _IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}
